import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { postingOf, useBusinessView, useFeatureSet, type Feature } from '@/lib/portal';

interface Entry {
  label: string;
  to: string;
  feature: Feature;
  /** Shown under approval posting as well (a draft for Outss), not only under direct posting. */
  draftable?: boolean;
}

/** What a client owner may start, by feature and posting mode (ADR-0017, document 05). */
const ENTRIES: Entry[] = [
  { label: 'Invoice', to: '/sales/invoices/new', feature: 'invoices', draftable: true },
  { label: 'Receive payment', to: '/sales/receive-payment/new', feature: 'invoices' },
  { label: 'Credit note', to: '/sales/credit-notes/new', feature: 'invoices' },
  { label: 'Sales receipt', to: '/sales/sales-receipts/new', feature: 'sales' },
  { label: 'Quote', to: '/sales/quotes/new', feature: 'sales' },
  { label: 'Bill', to: '/expenses/bills/new', feature: 'purchases', draftable: true },
  { label: 'Pay bills', to: '/expenses/pay-bills', feature: 'purchases' },
  { label: 'Expense', to: '/expenses/expenses/new', feature: 'expenses' },
  { label: 'Inventory qty adjustment', to: '/inventory/adjust', feature: 'inventory' },
];

/**
 * "+ New" in the Business view: what a client can do. Items the user cannot perform are hidden
 * (document 05); client viewers get no menu.
 */
export function BusinessNewMenu() {
  const view = useBusinessView();
  const features = useFeatureSet();
  const owner = view.role === 'client_owner' || view.preview;
  if (!owner) return null;
  const entries = ENTRIES.flatMap((e) => {
    const mode = postingOf(features, e.feature);
    if (mode === 'direct') return [{ ...e }];
    if (mode === 'approval' && e.draftable)
      return [{ ...e, label: `${e.label} (draft for approval)` }];
    return [];
  });
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="w-full justify-start gap-2" size="lg">
          <Plus className="size-4" /> New
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[260px]">
        <DropdownMenuItem asChild>
          <Link to="/documents/inbox">Upload bills and receipts</Link>
        </DropdownMenuItem>
        {entries.map((e) => (
          <DropdownMenuItem key={e.to} asChild>
            <Link to={e.to}>{e.label}</Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
